import { React, useState, useEffect, useRef } from "react";
import { NavLink, useParams, useNavigate, useLocation } from "react-router-dom";
import LibraryService from "../../../service/library-service";
import AuthService from "../../../service/auth-service";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import Loading from "../../dialog/loading/loading";
import "./library-article-style.css";
import SiteFooter from "../../custom/site-footer/site-footer";
import PageSeo from "../../custom/page-seo/page-seo";
import useIsMobile from "../../../hooks/useIsMobile";

//timestamp → 「編寫於YYYY/MM/DD」
const fmtWriteDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const p = (n) => String(n).padStart(2, "0");
  return `編寫於${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
};

const LibraryArticleCompontent = ({
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const { articleID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  //完成註冊後要回到原本這篇文章
  const registerFrom = location.pathname;
  const isMobile = useIsMobile();

  //用於管理區間塊
  const sectionRefs = useRef([]);

  //—— API 狀態 ——
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recommends, setRecommends] = useState([]);

  //—— 互動狀態（按讚 / 收藏 / 分享）——
  //  liked / collected：當前使用者是否已按過（從 /me/actions 拉）
  //  likeCount / collectCount / shareCount：實際計數，操作後 +/- 一
  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [collectCount, setCollectCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  //「已登入但 validCode !== '2'」中間狀態：先彈確認 dialog 提示，不無聲 navigate
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  //避免短時間重複點按（樂觀更新時的鎖）
  const [busy, setBusy] = useState({ like: false, collect: false, share: false });

  //—— 抓文章本體 + 初始化計數 ——
  useEffect(() => {
    if (!articleID) return;
    let alive = true;
    setLoading(true);
    setError("");
    LibraryService.getArticle(articleID)
      .then((res) => {
        if (!alive) return;
        const a = res.data?.data || null;
        setArticle(a);
        if (a) {
          setLikeCount(a.likes || 0);
          setCollectCount(a.collects || 0);
          setShareCount(a.shares || 0);
        }
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || "找不到文章");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [articleID]);

  //—— 拉「我是否按過 / 收藏過」這篇 ——
  //  optionalAuth 端點即使未登入也安全（回空），所以不需要先判斷登入狀態
  useEffect(() => {
    if (!articleID) return;
    let alive = true;
    LibraryService.getMyActions([articleID])
      .then((res) => {
        if (!alive) return;
        const d = res.data?.data || {};
        setLiked((d.like || []).includes(articleID));
        setCollected((d.collect || []).includes(articleID));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [articleID]);

  //—— 互動 helper：呼叫前的 auth 守門 ——
  //  回傳 true = 已通過、可繼續打 API；false = 已自行處理（彈 dialog）
  //
  //  雙層判斷（依使用者要求順序：先看登入、再看註冊）：
  //    1. 沒登入 / profile 缺漏 → 跳登入 dialog（login → success 後若 validCode != 2 會自動 navigate /register）
  //    2. 已登入但 validCode !== "2" → 跳「尚未完成註冊」確認 dialog（不要再要求重新登入）
  //    3. validCode === "2" → 通過
  const ensureCanInteract = () => {
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setShowLoginDialog(true);
      return false;
    }
    if (profile.userValidCode !== "2") {
      setShowRegisterPrompt(true);
      return false;
    }
    return true;
  };

  //登入 dialog 成功 callback：換完 JWT 後決定下一步
  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        //剛登入完成 → 若還沒完整註冊，把使用者帶去 /register
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        }
      }
    } catch (e) {
      console.error("[ArticlePage] firebase-login 失敗:", e);
    }
  };

  const handleLike = async () => {
    if (busy.like) return;
    if (!ensureCanInteract()) return;
    //樂觀更新：先動畫面、API 失敗再回滾
    const prevLiked = liked;
    const prevCount = likeCount;
    setLiked(!prevLiked);
    setLikeCount(prevCount + (prevLiked ? -1 : 1));
    setBusy((b) => ({ ...b, like: true }));
    try {
      const res = await LibraryService.toggleLike(articleID);
      if (res.data?.data) {
        setLiked(res.data.data.active);
        setLikeCount(res.data.data.count);
      }
    } catch (e) {
      //回滾
      setLiked(prevLiked);
      setLikeCount(prevCount);
      if (e?.response?.data?.needsLogin) setShowLoginDialog(true);
      else if (e?.response?.data?.needsRegister) setShowRegisterPrompt(true);
    } finally {
      setBusy((b) => ({ ...b, like: false }));
    }
  };

  const handleCollect = async () => {
    if (busy.collect) return;
    if (!ensureCanInteract()) return;
    const prevCollected = collected;
    const prevCount = collectCount;
    setCollected(!prevCollected);
    setCollectCount(prevCount + (prevCollected ? -1 : 1));
    setBusy((b) => ({ ...b, collect: true }));
    try {
      const res = await LibraryService.toggleCollect(articleID);
      if (res.data?.data) {
        setCollected(res.data.data.active);
        setCollectCount(res.data.data.count);
      }
    } catch (e) {
      setCollected(prevCollected);
      setCollectCount(prevCount);
      if (e?.response?.data?.needsLogin) setShowLoginDialog(true);
      else if (e?.response?.data?.needsRegister) setShowRegisterPrompt(true);
    } finally {
      setBusy((b) => ({ ...b, collect: false }));
    }
  };

  //分享：任何人都能按；用 Web Share API（手機有原生支援）/ 退而求其次複製連結
  //  剪貼簿格式：「標題\n連結」(line1 = title, line2 = url)，貼到 LINE / 訊息會比較完整
  const handleShare = async () => {
    if (busy.share) return;
    setBusy((b) => ({ ...b, share: true }));
    const shareURL = window.location.href;
    const shareTitle = article?.title || "元辰雲閣";
    const clipboardText = `${shareTitle}\n${shareURL}`;
    try {
      if (navigator.share) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareTitle,
            url: shareURL,
          });
        } catch {
          //使用者取消 share dialog → 不算分享、不計數
          return;
        }
      } else {
        await navigator.clipboard?.writeText?.(clipboardText);
        alert("已複製標題與連結");
      }
      //分享 / 複製成功才計數
      try {
        const res = await LibraryService.share(articleID);
        if (res.data?.data) setShareCount(res.data.data.count);
      } catch {
        //計數失敗不影響使用者體驗
      }
    } finally {
      setBusy((b) => ({ ...b, share: false }));
    }
  };

  //—— 抓推薦文章（同分類優先 → fallback 最新）——
  //  取到 article 後才會發；最多列 4 篇、排除自己
  useEffect(() => {
    if (!article) return;
    let alive = true;
    LibraryService.getArticles({
      category: article.category || "",
      sort: "new",
      skip: 0,
      limit: 5,
    })
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.data?.articles || [])
          .filter((a) => a.articleID !== article.articleID)
          .slice(0, 4);
        setRecommends(list);
      })
      .catch(() => alive && setRecommends([]));
    return () => {
      alive = false;
    };
  }, [article]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/library");
  };

  //—— 載入中 / 錯誤 / 找不到 ——
  if (loading) {
    return (
      <div className="library-article-page">
        <Loading />
      </div>
    );
  }
  if (error || !article) {
    return (
      <div className="library-article-page">
        <p className="article-status-msg error">{error || "找不到文章"}</p>
      </div>
    );
  }

  const writeDate = fmtWriteDate(article.publishedAt || article.createdAt);

  //—— 共用：頂部操作列 ——
  const TopAction = () => (
    <div className="top-action">
      <div className="back" onClick={goBack}>
        <img src="../images/ic-select-left.png" alt="" />
        <span>上一頁</span>
      </div>
      <div className="actions">
        <div className="action" onClick={handleLike}>
          <img
            src={
              liked
                ? "../images/ic-have-like.png"
                : "../images/ic-default-like.png"
            }
            alt=""
          />
          <span>{likeCount}</span>
        </div>
        <div className="action" onClick={handleCollect}>
          <img
            src={
              collected
                ? "../images/ic-have-collect.png"
                : "../images/ic-default-collect.png"
            }
            alt=""
          />
          <span>{collectCount}</span>
        </div>
        <div className="action" onClick={handleShare}>
          <img src="../images/ic-default-share.png" alt="" />
        </div>
      </div>
    </div>
  );

  //—— 共用：底部操作列（多顯示「分享出去」文字）——
  const BottomAction = () => (
    <div className="bottom-action">
      <div className="actions">
        <div className="action" onClick={handleLike}>
          <img
            src={
              liked
                ? "../images/ic-have-like.png"
                : "../images/ic-default-like.png"
            }
            alt=""
          />
          <span>{likeCount}</span>
        </div>
        <div className="action" onClick={handleCollect}>
          <img
            src={
              collected
                ? "../images/ic-have-collect.png"
                : "../images/ic-default-collect.png"
            }
            alt=""
          />
          <span>{collectCount}</span>
        </div>
        <div className="action" onClick={handleShare}>
          <img src="../images/ic-default-share.png" alt="" />
          <span>分享出去</span>
        </div>
      </div>
    </div>
  );

  //—— 共用：文章核心 ——
  const ArticleBody = () => (
    <>
      <div className="title-area">
        <span className="title">{article.title}</span>
        {writeDate && <span className="create-time">{writeDate}</span>}
        <div className="divide-line"></div>
      </div>

      <div className="content-area">
        <div
          className="article-html"
          dangerouslySetInnerHTML={{ __html: article.content || "" }}
        />
        {article.category && (
          <span className="article-tag">{article.category}</span>
        )}
      </div>
    </>
  );

  //—— 共用：推薦文章列表項目 ——
  const RecommendItem = ({ a, withImage }) => {
    const date = fmtWriteDate(a.publishedAt || a.createdAt);
    return (
      <div className="article">
        <div className="content">
          <NavLink
            className="nav-link"
            to={`/article/${a.articleID}`}
          >
            <div className="title">{a.title}</div>
            <div className="create-time">{date}</div>
            <div className="des">{a.summary}</div>
          </NavLink>

          <div className="actions">
            <div className="action">
              <img src="../images/ic-default-like.png" alt="" />
              <span>{a.likes || 0}</span>
            </div>
            <div className="action">
              <img src="../images/ic-default-collect.png" alt="" />
              <span>{a.collects || 0}</span>
            </div>
            <div className="action">
              <img src="../images/ic-default-share.png" alt="" />
            </div>
          </div>
        </div>

        {withImage && (
          <NavLink
            className="nav-img-link"
            to={`/article/${a.articleID}`}
          >
            <div className="holder">
              {a.coverImage && <img src={a.coverImage} alt="" />}
            </div>
          </NavLink>
        )}
      </div>
    );
  };

  return (
    <div className="library-article-page">
      <PageSeo
        title={article?.title || "命理文章"}
        description={
          article?.summary ||
          article?.preview ||
          "元辰雲閣命理知識庫文章，看懂自己的命盤從這裡開始。"
        }
        path={`/article/${articleID || ""}`}
      />
      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="article-main">
            <div className="article-content">
              <TopAction />
              <ArticleBody />
              <BottomAction />
            </div>

            {recommends.length > 0 && (
              <div className="recommand-articles">
                <span className="title">你可能也想看</span>
                {recommends.map((a) => (
                  <RecommendItem key={a.articleID} a={a} withImage />
                ))}
              </div>
            )}
          </div>

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {/* 手機板佈局 */}
      {isMobile && (
        <div className="mobile-main">
          <div className="article-main">
            <div className="article-content">
              <TopAction />
              <ArticleBody />
              <BottomAction />
            </div>

            {recommends.length > 0 && (
              <div className="recommand-articles">
                <span className="title">你可能也想看</span>
                {recommends.map((a) => (
                  <RecommendItem key={a.articleID} a={a} withImage={false} />
                ))}
              </div>
            )}
          </div>

          <div className="divide"></div>

          <SiteFooter />
        </div>
      )}

      {/* 按讚 / 收藏點下去未登入時跳出；關掉 → 留在當前頁 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊：明確提示，再讓使用者選擇是否前往；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="按讚 / 收藏需要完成註冊才能使用，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default LibraryArticleCompontent;
